import { useFeatureFlagContext } from 'webReservations/featureFlags';
import { RESERVATION_EDIT_PATH } from 'webReservations/paths';
import { Button, ButtonVariants } from '@components/button/Button';
import { Divider } from '@components/divider/Divider';
import { LinkStyledAsButton } from '@components/linkStyledAsButton/LinkStyledAsButton';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { AcceptTopOffer } from './AcceptTopOffer';
import type { ReservationWithDetails, TopOffer } from './apiHelpers';
import { GuestCancelConfirmationModal } from './GuestCancelConfirmationModal';
import { ListPrice } from './ListPrice';
import { ReceiptDetails } from './ReceiptDetails';
import styles from './ReservationDetailsPanel.scss';
import { ReservationNote } from './ReservationNote';
import { RestaurantDetails } from './RestaurantDetails';

interface ReservationDetailsPanelProps {
  fetchReservation: () => void;
  reservation: ReservationWithDetails;
  reservationId: string;
  topOffer: TopOffer;
}

export const ReservationDetailsPanel = ({
  fetchReservation,
  reservation,
  reservationId,
  topOffer,
}: ReservationDetailsPanelProps) => {
  const { isEnabled } = useFeatureFlagContext();
  const {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useIsOpen();

  const isActive = reservation.status === 'active';
  const allowResale = isActive && reservation.canBeResold;
  const isEventReservation = Boolean(reservation.restaurantEvent);
  const eventLocationText =
    reservation.restaurantEvent?.location?.trim() || null;
  const eventDirectionsText =
    reservation.restaurantEvent?.directions?.trim() || null;
  const restaurantForDetails =
    isEventReservation && eventLocationText
      ? { ...reservation.restaurant, address: eventLocationText }
      : reservation.restaurant;
  const editPathParams = new URLSearchParams({
    date: reservation.date,
    guestCount: reservation.guestCount.toString(),
  });

  return (
    <>
      <Divider className={styles.divider} />
      {isActive && !reservation.restaurantEvent && (
        <>
          <section className={styles.modifyReservationActions}>
            {isEnabled('guestDrivenReservationChanges') && (
              <LinkStyledAsButton
                label="Change Reservation"
                variant={ButtonVariants.Secondary}
                to={`${RESERVATION_EDIT_PATH}?${editPathParams.toString()}`}
              />
            )}
            <Button
              isDisabled={!reservation.isCancelableByGuest}
              label="Cancel Reservation"
              onClick={openModal}
              variant={ButtonVariants.Tertiary}
            />
            <GuestCancelConfirmationModal
              closeModal={closeModal}
              isOpen={isModalOpen}
              reservation={reservation}
              refreshReservation={fetchReservation}
              cancellationPolicy={reservation.transaction.cancellationPolicy}
            />
          </section>
          <Divider className={styles.divider} />
        </>
      )}
      {allowResale && (
        <>
          <section>
            <h2 className={cx(typography.h4, styles.sellIt)}>Sell It</h2>
            <div className={styles.card}>
              <AcceptTopOffer offer={topOffer} reservation={reservation} />
            </div>
            <div className={styles.card}>
              <ListPrice
                fetchReservation={fetchReservation}
                price={reservation.buyNowPrice}
                reservationId={reservationId}
              />
            </div>
          </section>
          <Divider className={styles.divider} />
        </>
      )}
      <ReservationNote reservation={reservation} />
      <Divider className={styles.divider} />
      <RestaurantDetails
        restaurant={restaurantForDetails}
        hideAddress={isEventReservation && !eventLocationText}
        directionsText={isEventReservation ? eventDirectionsText : null}
      />
      <Divider className={styles.divider} />
      <ReceiptDetails reservation={reservation} />
    </>
  );
};
